/* eslint-disable no-restricted-imports */
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  FormControl,
  FormControlLabel,
} from '@mui/material';
import { ReactNode } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FieldHelpText } from '../../field/field-helper-text-caption';

type WarningProps = {
  warning?: boolean;
  warningText?: string;
};

export interface CheckboxProps extends MuiCheckboxProps, WarningProps {
  name: string;
  label?: string;
  helperText?: ReactNode;
  errorText?: ReactNode;
  inline?: boolean;
}

export const Checkbox = ({ name, color = 'primary', ...checkboxProps }: MuiCheckboxProps) => {
  return <MuiCheckbox {...checkboxProps} name={name} color={color} />;
};

export interface ControlledCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
> extends Omit<CheckboxProps, 'name' | 'errorText'> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
}

export const ControlledCheckbox = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  inline,
  warning,
  helperText,
  warningText,
  ...checkboxProps
}: ControlledCheckboxProps<TFieldValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const hasError = !!fieldState.error;
        const hasWarning = warning && !hasError;
        const displayHelperText =
          fieldState.error?.message ?? (warning && warningText ? warningText : helperText);
        const isDisabled = checkboxProps.disabled ?? field.disabled;
        const isHelpTextVisible = !hasError && !hasWarning && !!helperText;

        return (
          <FormControl
            error={hasError}
            disabled={isDisabled}
            color={hasWarning ? 'warning' : undefined}
            {...(inline && { className: 'MuiFormControl-noMargin', sx: { height: 36 } })}
          >
            <FormControlLabel
              disabled={isDisabled}
              control={<Checkbox {...field} {...checkboxProps} name={name} checked={field.value} />}
              label={label}
            />
            {label && (
              <FieldHelpText
                sx={{
                  mt: isHelpTextVisible ? '-8px' : -2,
                  ml: isHelpTextVisible ? '-14px' : '18px',
                }}
              >
                {displayHelperText}
              </FieldHelpText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default ControlledCheckbox;
